import { API_ENDPOINTS } from '@tsgecart/config';
import { adminApiFetch, apiBaseUrl } from '../../api/admin-client.js';
import type {
  AdminOrderSummary,
  AdminOrderDetailResponse,
  OrderListParams,
  ChangeOrderStatusInput,
  CancelOrderInput,
  CancellationReason,
} from '../types/orders.js';

export interface FetchOptions {
  token: string;
  signal?: AbortSignal;
}

function buildQuery(params: Record<string, string | number | boolean | string[] | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export async function fetchAdminOrders(
  params: OrderListParams,
  options: FetchOptions,
): Promise<AdminOrderSummary[]> {
  const query = buildQuery({
    search: params.search,
    status: params.status,
    paymentStatus: params.paymentStatus,
    paymentMethod: params.paymentMethod,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    minTotalPaise: params.minTotalPaise,
    maxTotalPaise: params.maxTotalPaise,
    sortField: params.sortField,
    sortDirection: params.sortDirection,
    limit: params.limit,
    offset: params.offset,
  });
  return adminApiFetch<AdminOrderSummary[]>(`${API_ENDPOINTS.adminOrders}${query}`, options);
}

export async function fetchAdminOrderDetail(
  orderNumber: string,
  options: FetchOptions,
): Promise<AdminOrderDetailResponse> {
  return adminApiFetch<AdminOrderDetailResponse>(
    `${API_ENDPOINTS.adminOrders}/${encodeURIComponent(orderNumber)}`,
    options,
  );
}

export async function changeAdminOrderStatus(
  orderNumber: string,
  input: ChangeOrderStatusInput,
  options: FetchOptions,
): Promise<AdminOrderDetailResponse['order']> {
  return adminApiFetch(`${API_ENDPOINTS.adminOrders}/${encodeURIComponent(orderNumber)}/status`, {
    ...options,
    method: 'POST',
    body: input,
  });
}

export async function cancelAdminOrder(
  orderNumber: string,
  input: CancelOrderInput,
  options: FetchOptions,
): Promise<AdminOrderDetailResponse['order']> {
  return adminApiFetch(`${API_ENDPOINTS.adminOrders}/${encodeURIComponent(orderNumber)}/cancel`, {
    ...options,
    method: 'POST',
    body: input,
  });
}

export async function fetchOrderCancellationReasons(
  options: FetchOptions,
): Promise<CancellationReason[]> {
  return adminApiFetch<CancellationReason[]>(
    `${API_ENDPOINTS.adminOrders}/cancellation-reasons`,
    options,
  );
}

/** Triggers a browser download of the bounded CSV export -- a plain authenticated fetch + blob download, not routed through `adminApiFetch` (which assumes a JSON envelope; this endpoint returns raw `text/csv`). */
export async function downloadAdminOrdersExport(
  params: OrderListParams,
  token: string,
): Promise<void> {
  const query = buildQuery({
    search: params.search,
    status: params.status,
    paymentStatus: params.paymentStatus,
    paymentMethod: params.paymentMethod,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    minTotalPaise: params.minTotalPaise,
    maxTotalPaise: params.maxTotalPaise,
  });
  const response = await fetch(`${apiBaseUrl}${API_ENDPOINTS.adminOrders}/export${query}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Export failed.');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'orders-export.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
